import { useState } from "react";
import {
  BulkInvitationCreate,
  BulkInvitationResponse,
  getPayrollAgenticAPI,
  Recipient,
  RecipientRole,
  BulkInvitationCreateOrgCategory,
  BulkInvitationCreateDeliveryMethod,
} from "../../../../generated/core-api";
import { PendingOrgInviteBulk } from "./useOrgInviteBulkCsvParser";

interface CreateBulkInvitationsParams {
  orgId: string;
  orgCategory: BulkInvitationCreateOrgCategory;
  deliveryMethod?: BulkInvitationCreateDeliveryMethod;
}

export function useOrgInviteBulkManager() {
  const [pendingInvites, setPendingInvites] = useState<PendingOrgInviteBulk[]>(
    []
  );
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [isCreatingInvitations, setIsCreatingInvitations] = useState(false);
  const [creationResult, setCreationResult] =
    useState<BulkInvitationResponse | null>(null);

  const setPendingData = (
    invites: PendingOrgInviteBulk[],
    errors: string[] = []
  ) => {
    setPendingInvites(invites);
    setParseErrors(errors);
    setCreationResult(null);
  };

  const createBulkInvitations = async (
    params: CreateBulkInvitationsParams
  ): Promise<BulkInvitationResponse | null> => {
    if (pendingInvites.length === 0) {
      return null;
    }

    setIsCreatingInvitations(true);
    setCreationResult(null);

    try {
      // Transform PendingOrgInviteBulk to Recipient
      const recipients: Recipient[] = pendingInvites.map((invite) => ({
        email: invite.email,
        name: invite.name,
        role: invite.role as RecipientRole,
      }));

      const bulkInvitationData: BulkInvitationCreate = {
        org_id: params.orgId,
        org_category: params.orgCategory,
        recipients,
        delivery_method: params.deliveryMethod || "email",
      };

      const api = getPayrollAgenticAPI();
      const response = await api.createBulkInvitations(bulkInvitationData);

      setCreationResult(response);
      return response;
    } catch (error) {
      console.error("Failed to create bulk invitations:", error);
      // Could set an error state here if needed
      throw error;
    } finally {
      setIsCreatingInvitations(false);
    }
  };

  const clearData = () => {
    setPendingInvites([]);
    setParseErrors([]);
    setCreationResult(null);
  };

  const removeInvite = (index: number) => {
    setPendingInvites((prev) => prev.filter((_, i) => i !== index));
  };

  const updateInvite = (index: number, updatedInvite: PendingOrgInviteBulk) => {
    setPendingInvites((prev) =>
      prev.map((invite, i) => (i === index ? updatedInvite : invite))
    );
  };

  return {
    pendingInvites,
    parseErrors,
    isCreatingInvitations,
    creationResult,
    setPendingData,
    createBulkInvitations,
    clearData,
    removeInvite,
    updateInvite,
    hasData: pendingInvites.length > 0,
    hasErrors: parseErrors.length > 0,
  };
}
